import React, { useState } from 'react';
import { Container, Grid, Typography, Tab, Tabs, Paper, useTheme } from '@mui/material';
import { ChemicalRoute, chemicalRoutesData, chemicalCategories } from '../../types';
import ChemicalRouteCard from './ChemicalRouteCard';
import ChemicalFlowDiagram from './ChemicalFlowDiagram';
import SwipeableFlowShell from '../common/SwipeableFlowShell';
import CategoryTabLabel from '../common/CategoryTabLabel';

const ChemicalView: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<ChemicalRoute | null>(
  () => chemicalRoutesData.find((route) => route.category === chemicalCategories[0].category) ?? null
);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const newCategory = chemicalCategories[newValue];
    const firstRoute = chemicalRoutesData.find((route) => route.category === newCategory.category);
    setSelectedRoute(firstRoute ?? null);
  };

  const handleRouteSelect = (route: ChemicalRoute) => {
    setSelectedRoute(route);
  };

  const filteredRoutes = chemicalRoutesData.filter((route) => {
    const category = chemicalCategories[activeTab];
    return route.category === category.category;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: isDarkMode ? '#1b5e20' : '#e3f2fd',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            color: isDarkMode ? '#66bb6a' : '#2e7d32',
            fontWeight: 600
          }}
        >
          化学工业技术路线
        </Typography>
        <Typography variant="body1" color="text.secondary">
          涵盖氯碱工业、氟化工、硫酸工业、合成氨工业等基础化工领域，以及精细化工（硝基胍等氰胺产业链）、
          环保（芬顿氧化水处理）方向，展示电解、聚合、催化、硝化、高级氧化等核心工艺路线，
          是现代工业体系的重要组成部分。
        </Typography>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
            },
            '& .Mui-selected': {
              backgroundColor: isDarkMode ? '#2e7d3230' : '#e3f2fd',
            },
          }}
        >
          {chemicalCategories.map((category, categoryIndex) => (
            <Tab
              key={category.id}
              label={
                <CategoryTabLabel
                  label={category.label}
                  routes={
                    chemicalRoutesData
                      .filter((route) => route.category === category.category)
                      .map((route) => ({ id: route.id, name: route.name }))
                  }
                  selectedRouteId={selectedRoute?.id}
                  onSelectRoute={(routeId) => {
                    handleTabChange(null as unknown as React.SyntheticEvent, categoryIndex);
                    const target = chemicalRoutesData.find((route) => route.id === routeId);
                    if (target) setSelectedRoute(target);
                  }}
                />
              }
            />
          ))}
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {selectedRoute && (
          <Grid item xs={12}>
            <SwipeableFlowShell
              routes={filteredRoutes}
              selectedId={selectedRoute.id}
              onSelect={(id) => setSelectedRoute(filteredRoutes.find((r) => r.id === id) ?? null)}
            >
              <ChemicalFlowDiagram route={selectedRoute} />
            </SwipeableFlowShell>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: isDarkMode ? '#66bb6a' : '#2e7d32',
              fontWeight: 600
            }}
          >
            {chemicalCategories[activeTab].label} - 技术路线
          </Typography>
          <Grid container spacing={2}>
            {filteredRoutes.map((route) => (
              <Grid item xs={12} sm={6} lg={4} key={route.id}>
                <ChemicalRouteCard
                  route={route}
                  onClick={handleRouteSelect}
                  isSelected={selectedRoute?.id === route.id}
                />
              </Grid>
            ))}
          </Grid>
          {filteredRoutes.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5' }}>
              <Typography color="text.secondary">
                该分类下暂无技术路线数据
              </Typography>
            </Paper>
          )}
        </Grid>

      </Grid>

      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: isDarkMode ? '#4a3728' : '#fff3e0',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: isDarkMode ? '#ffb74d' : '#ff9800',
            fontWeight: 600
          }}
        >
          使用提示
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • 点击下方路线卡片查看详细工艺流程图
          <br />
          • 在流程图中点击工艺步骤节点，下方将显示该步骤的详细信息
          <br />
          • 详情信息包括步骤描述、关键参数和注意事项
          <br />
          • 使用鼠标滚轮或控制按钮缩放流程图
          <br />• 拖动流程图可查看完整工艺流程
        </Typography>
      </Paper>
    </Container>
  );
};

export default ChemicalView;
